import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Package, Calculator, Scale, Box, ArrowRight, Laptop, Smartphone, SprayCan, ArrowLeftRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import ThemeToggle from "@/components/ThemeToggle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIXED_ITEM_ICONS = { "Laptop": Laptop, "Perfume": SprayCan, "Telephone": Smartphone };

const QuotePage = () => {
  const [quoteType, setQuoteType] = useState("custom");
  const [weight, setWeight] = useState("");
  const [volume, setVolume] = useState("");
  const [selectedItem, setSelectedItem] = useState("");
  const [fixedRates, setFixedRates] = useState([]);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingRates, setLoadingRates] = useState(true);
  const [weightUnit, setWeightUnit] = useState("lbs");

  const KG_TO_LBS = 2.20462;
  const LBS_TO_KG = 1 / KG_TO_LBS;

  useEffect(() => { fetchRates(); }, []);

  const fetchRates = async () => {
    try {
      const res = await axios.get(`${API}/quote/rate`);
      setFixedRates(res.data.fixed_rates || []);
    } catch (err) { console.error("Failed to fetch rates:", err); }
    finally { setLoadingRates(false); }
  };

  const handleCalculate = async (e) => {
    e.preventDefault();
    if (quoteType === "custom") {
      if (!weight && !volume) { toast.error("Please enter weight or volume"); return; }
      setLoading(true);
      try {
        const weightInLbs = weightUnit === "kg" ? (parseFloat(weight) || 0) * KG_TO_LBS : (parseFloat(weight) || 0);
        const res = await axios.post(`${API}/quote`, { weight: Math.round(weightInLbs * 100) / 100, volume: parseFloat(volume) || 0 });
        setQuote({ ...res.data, input_unit: weightUnit, input_weight: parseFloat(weight) || 0 });
      } catch (err) { toast.error(err.response?.data?.detail || "Failed to calculate quote"); }
      finally { setLoading(false); }
    } else {
      if (!selectedItem) { toast.error("Please select an item"); return; }
      const item = fixedRates.find(r => r.name === selectedItem);
      if (item) { setQuote({ estimated_price: item.price, item_name: item.name, calculation_method: "fixed" }); }
    }
  };

  const resetQuote = () => { setQuote(null); setWeight(""); setVolume(""); setSelectedItem(""); setWeightUnit("lbs"); };
  const handleQuoteTypeChange = (value) => { setQuoteType(value); resetQuote(); };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex justify-between items-center">
          <Link to="/" className="flex items-center"><img src="/logo-v2.png" alt="SHIPP SMART" className="h-14 w-auto rounded-md" /></Link>
          <nav className="flex items-center gap-2">
            <Link to="/contact"><Button variant="ghost" className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white text-sm">Contact</Button></Link>
            <ThemeToggle />
            <Link to="/customer"><Button className="bg-orange-600 hover:bg-orange-500 text-white text-sm rounded-lg">Login</Button></Link>
          </nav>
        </div>
      </header>

      <div className="relative h-48 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1761133381018-aed5063d22fe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxzZW1pJTIwdHJ1Y2slMjBoaWdod2F5fGVufDB8fHx8MTc4MTAyNjU5N3ww&ixlib=rb-4.1.0&q=85')` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/60 to-slate-50 dark:to-slate-950" />
        <div className="relative z-10 h-full flex items-center justify-center">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <p className="text-orange-400 font-medium text-xs tracking-[0.25em] uppercase mb-2">Shipping Calculator</p>
            <h1 className="font-outfit font-bold text-3xl sm:text-4xl text-white tracking-tight">Get a Quote</h1>
          </motion.div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-5 gap-8">
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="md:col-span-3">
            <Card className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-xl shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="font-outfit font-semibold text-xl flex items-center gap-2.5 text-slate-900 dark:text-white">
                  <div className="w-9 h-9 rounded-lg bg-orange-50 dark:bg-orange-500/10 flex items-center justify-center">
                    <Scale className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                  </div>
                  Calculate Your Rate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCalculate} className="space-y-5">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500 dark:text-slate-400">Quote Type</Label>
                    <Select value={quoteType} onValueChange={handleQuoteTypeChange}>
                      <SelectTrigger className="h-12 rounded-lg" data-testid="quote-type-select"><SelectValue placeholder="Select quote type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed" data-testid="quote-type-fixed">Fixed Rate (Common Items)</SelectItem>
                        <SelectItem value="custom" data-testid="quote-type-custom">Custom Rate (Weight/Volume)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {quoteType === "fixed" ? (
                    <div className="space-y-4">
                      <Label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500 dark:text-slate-400 flex items-center gap-2"><Package className="w-4 h-4" /> Select Item</Label>
                      <div className="grid grid-cols-3 gap-3">
                        {fixedRates.map((item) => {
                          const Icon = FIXED_ITEM_ICONS[item.name] || Package;
                          const isSelected = selectedItem === item.name;
                          return (
                            <button key={item.name} type="button" onClick={() => setSelectedItem(item.name)}
                              className={`p-5 rounded-xl border-2 transition-all text-center ${isSelected ? "border-orange-500 bg-orange-50 dark:bg-orange-500/10 shadow-sm" : "border-slate-200 dark:border-slate-700 hover:border-orange-300 bg-white dark:bg-slate-800"}`}
                              data-testid={`fixed-item-card-${item.name.toLowerCase()}`}>
                              <Icon className={`w-7 h-7 mx-auto mb-2 ${isSelected ? "text-orange-600 dark:text-orange-400" : "text-slate-400"}`} />
                              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{item.name}</p>
                              <p className={`text-lg font-bold mt-1 ${isSelected ? "text-orange-600 dark:text-orange-400" : "text-slate-900 dark:text-white"}`}>${item.price}</p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500 dark:text-slate-400 flex items-center gap-2"><Scale className="w-4 h-4" /> Weight</Label>
                        <div className="relative">
                          <Input type="number" step="0.1" min="0" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder={weightUnit === "lbs" ? "e.g., 5.5" : "e.g., 2.5"} className="h-12 text-lg font-mono rounded-lg pr-20" data-testid="quote-weight-input" />
                          <button type="button" onClick={() => {
                            const newUnit = weightUnit === "lbs" ? "kg" : "lbs";
                            if (weight) { const converted = newUnit === "kg" ? (parseFloat(weight) * LBS_TO_KG) : (parseFloat(weight) * KG_TO_LBS); setWeight(String(Math.round(converted * 100) / 100)); }
                            setWeightUnit(newUnit); setQuote(null);
                          }} className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-100 dark:bg-slate-700 hover:bg-orange-100 dark:hover:bg-orange-500/20 text-slate-600 dark:text-slate-300 hover:text-orange-600 dark:hover:text-orange-400 text-xs font-bold uppercase tracking-wider transition-colors" data-testid="weight-unit-toggle">
                            <ArrowLeftRight className="w-3 h-3" />{weightUnit}
                          </button>
                        </div>
                        {weightUnit === "kg" && weight && <p className="text-slate-400 text-xs font-mono">= {(parseFloat(weight) * KG_TO_LBS).toFixed(2)} lbs</p>}
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500 dark:text-slate-400 flex items-center gap-2"><Box className="w-4 h-4" /> Volume (ft³)</Label>
                        <Input type="number" step="0.01" min="0" value={volume} onChange={(e) => setVolume(e.target.value)} placeholder="e.g., 0.5" className="h-12 text-lg font-mono rounded-lg" data-testid="quote-volume-input" />
                        <p className="text-slate-400 text-xs">L × W × H in feet</p>
                      </div>
                    </div>
                  )}

                  <Button type="submit" disabled={loading} className="w-full h-12 bg-orange-600 hover:bg-orange-500 text-white font-medium rounded-lg text-base transition-colors" data-testid="calculate-quote-btn">
                    {loading ? "Calculating..." : "Get Quote"}<ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="md:col-span-2">
            {quote ? (
              <Card className="border-orange-200 dark:border-orange-500/30 bg-gradient-to-b from-orange-50 dark:from-orange-500/5 to-white dark:to-slate-900 rounded-xl shadow-sm">
                <CardContent className="p-8">
                  <p className="text-xs font-bold tracking-[0.2em] uppercase text-orange-600 dark:text-orange-400 mb-6">Your Quote</p>
                  <div className="text-center py-6">
                    <p className="text-slate-500 dark:text-slate-400 text-sm mb-1">Estimated Price</p>
                    <p className="font-outfit font-bold text-5xl text-slate-900 dark:text-white" data-testid="quote-result">${quote.estimated_price}</p>
                  </div>
                  <div className="space-y-3 border-t border-orange-200 dark:border-orange-500/20 pt-6 mt-4">
                    {quote.calculation_method === "fixed" ? (
                      <>
                        <div className="flex justify-between text-sm"><span className="text-slate-500 dark:text-slate-400">Item</span><span className="font-medium text-slate-900 dark:text-white">{quote.item_name}</span></div>
                        <div className="flex justify-between text-sm"><span className="text-slate-500 dark:text-slate-400">Rate Type</span><span className="font-medium text-orange-600 dark:text-orange-400">Fixed</span></div>
                      </>
                    ) : (
                      <>
                        <div className="flex justify-between text-sm"><span className="text-slate-500 dark:text-slate-400">Weight</span><span className="font-mono font-medium text-slate-900 dark:text-white">{quote.input_unit === "kg" ? `${quote.input_weight} kg (${quote.weight} lbs)` : `${quote.weight} lbs`}</span></div>
                        <div className="flex justify-between text-sm"><span className="text-slate-500 dark:text-slate-400">Volume</span><span className="font-mono font-medium text-slate-900 dark:text-white">{quote.volume} ft³</span></div>
                        <div className="flex justify-between text-sm"><span className="text-slate-500 dark:text-slate-400">Billable</span><span className="font-mono font-medium text-slate-900 dark:text-white">{quote.billable_value} ({quote.calculation_method === "weight" ? "lbs" : "ft³"})</span></div>
                        <div className="flex justify-between text-sm"><span className="text-slate-500 dark:text-slate-400">Rate</span><span className="font-mono font-medium text-slate-900 dark:text-white">${quote.rate_per_unit}/{quote.calculation_method === "weight" ? "lbs" : "ft³"}</span></div>
                      </>
                    )}
                  </div>
                  <Link to="/customer" className="block mt-6">
                    <Button className="w-full bg-slate-900 dark:bg-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-100 text-white rounded-lg" data-testid="proceed-to-ship-btn">
                      Proceed to Ship <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-xl shadow-sm">
                <CardContent className="p-8 text-center py-16">
                  <div className="w-16 h-16 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4">
                    <Calculator className="w-8 h-8 text-slate-300 dark:text-slate-600" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-outfit font-semibold text-lg text-slate-500 dark:text-slate-400 mb-2">{quoteType === "fixed" ? "Select an Item" : "Enter Details"}</h3>
                  <p className="text-slate-400 dark:text-slate-500 text-sm">{quoteType === "fixed" ? "Choose an item to see the price" : "Fill in weight or volume"}</p>
                </CardContent>
              </Card>
            )}

            <Card className="border-slate-200 dark:border-slate-800 bg-slate-900 dark:bg-slate-800 text-white rounded-xl mt-6">
              <CardContent className="p-6">
                <h3 className="font-outfit font-semibold text-sm uppercase tracking-wider mb-3">How Pricing Works</h3>
                <p className="text-slate-400 text-sm leading-relaxed"><strong className="text-slate-200">Fixed Rate:</strong> Common items have a set price.</p>
                <p className="text-slate-400 text-sm leading-relaxed mt-2"><strong className="text-slate-200">Custom Rate:</strong> Based on the greater of weight or volumetric weight for fair pricing.</p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-800 py-8 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-slate-400 text-sm">&copy; {new Date().getFullYear()} SHIPP SMART</p>
        </div>
      </footer>
    </div>
  );
};

export default QuotePage;
